'use client';

import type {
  ChildrenCategory,
  FormSchema,
} from '~/landing/app/(garance)/cagnup/schema';

import {
  Button,
  Card,
  CardContent,
  FieldWrapper,
  FormField,
  Typography,
} from '@polyseme/design-system';
import { formatCurrency } from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  calculateFamilyBudget,
  calculateTotalCashbackForAllCategories,
} from '~/landing/app/(garance)/cagnup/calculations';
import { CounterCard } from '~/landing/app/(garance)/cagnup/components';
import { categories } from '~/landing/app/(garance)/cagnup/constants';
import { images } from '~/landing/assets';

type FamilyFormProps = {
  scrollToEstimatedSavings: () => void;
};

export const FamilyForm = forwardRef<HTMLDivElement, FamilyFormProps>(
  ({ scrollToEstimatedSavings }, ref) => {
    const t = useTranslations('landing.cagnup');
    const form = useFormContext<FormSchema>();

    const family = form.watch('family');
    const totalSavings = calculateTotalCashbackForAllCategories(family);

    const handleFamilyChange = (
      fieldId: ChildrenCategory,
      newValue: number
    ) => {
      const updatedFamily = {
        ...form.getValues('family'),
        [fieldId]: newValue,
      };

      form.setValue(`family.${fieldId}`, newValue);

      const newBudget = calculateFamilyBudget(updatedFamily);

      form.setValue('budget.subscriptions', newBudget.subscriptions);
      form.setValue('budget.food', newBudget.food);
      form.setValue('budget.housing', newBudget.housing);
      form.setValue('budget.shopping', newBudget.shopping);
    };

    return (
      <section ref={ref} className="relative overflow-hidden bg-primary/20">
        <div className="absolute inset-0 z-0">
          <Image
            fill
            priority
            alt=""
            className="object-cover brightness-50 [object-position:center_25%]"
            quality={100}
            sizes="100vw"
            src={images.cagnup.hero}
          />
        </div>

        <div className="relative z-30 mx-auto w-full max-w-screen-2xl px-4 py-14 text-center md:px-10 md:text-start">
          <div>
            <h1 className="font-secondary text-4xl font-normal text-white md:whitespace-pre-wrap md:text-7xl">
              {t('hero.title')}
            </h1>
            <Typography
              className="mt-4 text-white md:whitespace-pre-wrap"
              variant="title-6"
            >
              {t('hero.subtitle')}
            </Typography>
          </div>

          <div className="mt-16 space-y-4">
            <h2 className="text-white">{t('familyForm.title')}</h2>

            <div className="mx-auto grid gap-6 sm:grid-cols-2 xl:flex xl:flex-wrap">
              {categories.map(({ activeIconClass, icon, id }) => (
                <FormField
                  key={id}
                  control={form.control}
                  name={`family.${id}`}
                  render={({ field }) => (
                    <FieldWrapper>
                      <CounterCard
                        activeIconClass={activeIconClass}
                        aria-label={t(`categories.${id}.sr-only-label`)}
                        className="mx-auto h-full max-w-72 xl:mx-[unset]"
                        description={t(`categories.${id}.description`)}
                        icon={icon}
                        label={t(`categories.${id}.label`)}
                        max={10}
                        min={0}
                        unit={t(`familyForm.unit`, { count: field.value })}
                        value={field.value}
                        onChange={(value) => handleFamilyChange(id, value)}
                      />
                    </FieldWrapper>
                  )}
                />
              ))}
              <Card className="xl:max-w-76 mx-auto size-full max-w-72 xl:mx-[unset] xl:ml-14 2xl:ml-20">
                <CardContent className="flex size-full flex-col px-8 py-6 text-center">
                  <Typography
                    className="mb-6 whitespace-pre-wrap"
                    variant="title-5"
                  >
                    {t('familyForm.result.title')}
                  </Typography>
                  <Typography variant="title-4">
                    {formatCurrency(totalSavings)}
                  </Typography>
                  <Typography className="mb-6" variant="body">
                    {t('familyForm.result.subtitle')}
                  </Typography>
                  <Button
                    className="mx-auto mt-auto w-min"
                    onClick={scrollToEstimatedSavings}
                  >
                    {t('familyForm.result.cta')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

FamilyForm.displayName = 'FamilyForm';
